import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useOrganization } from '@/contexts/OrganizationContext';
import { supabase } from '@/integrations/supabase/client';
import {
  getSecondsSince,
  identifyAmplitudeUser,
  setAmplitudeGroups,
  setAmplitudeUserId,
  trackAmplitudeEvent,
  type AmplitudeGroupDefinition,
} from '@/lib/amplitudeAnalytics';
import {
  getAnalyticsRouteDescriptor,
  resolveAnalyticsContext,
  type AnalyticsOrgType,
} from '@/lib/analyticsContext';

type AnalyticsBridgeOrganization = {
  id: string;
  name?: string | null;
  settings?: Record<string, unknown>;
};

const EMPTY_AMPLITUDE_GROUPS = {
  msp_org: null,
  customer_org: null,
  supplier_org: null,
} as const;

function readSetting(settings: Record<string, unknown> | undefined, key: string) {
  const value = settings?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function hasVerifiedMfaFactor(user: { factors?: Array<{ status?: string | null }> } | null) {
  return Boolean(user?.factors?.some((factor) => factor.status === 'verified'));
}

function oncePerBrowser(storage: Storage, key: string) {
  if (storage.getItem(key)) {
    return false;
  }
  storage.setItem(key, '1');
  return true;
}

function buildGroupDefinition({
  organization,
  orgType,
}: {
  organization: AnalyticsBridgeOrganization | null;
  orgType: AnalyticsOrgType;
}): AmplitudeGroupDefinition | null {
  if (!organization?.id || orgType === 'unknown') {
    return null;
  }

  return {
    id: organization.id,
    properties: {
      'Org ID': organization.id,
      'Org Type': orgType,
      'Org Name': organization.name ?? null,
    },
  };
}

type PendingSignedIn = {
  dedupeKey: string;
  userId: string;
  mfaEnabled: boolean;
};

type PendingEmailVerified = {
  dedupeKey: string;
  userId: string;
  createdAt?: string | null;
};

export function AmplitudeAnalyticsBridge() {
  const location = useLocation();
  const {
    user,
    organization,
    membership,
    bootstrapSubscription,
    isMspOrg,
    isMspContext,
    mspHomeOrg,
  } = useOrganization();
  const [pendingSignedIn, setPendingSignedIn] = useState<PendingSignedIn | null>(null);
  const [pendingEmailVerified, setPendingEmailVerified] = useState<PendingEmailVerified | null>(null);
  const lastGroupsKeyRef = useRef<string | null>(null);
  const previousContextRef = useRef<ReturnType<typeof resolveAnalyticsContext> | null>(null);

  useEffect(() => {
    setAmplitudeUserId(user?.id);
  }, [user?.id]);

  useEffect(() => {
    if (!user || !organization || !membership) {
      const groupsKey = JSON.stringify(EMPTY_AMPLITUDE_GROUPS);
      if (lastGroupsKeyRef.current !== groupsKey) {
        lastGroupsKeyRef.current = groupsKey;
        setAmplitudeGroups(EMPTY_AMPLITUDE_GROUPS);
      }
      return;
    }

    const accountType = readSetting(organization.settings, 'account_type');
    const primaryComplianceFramework = readSetting(organization.settings, 'primary_compliance_framework');
    const mfaEnabled = hasVerifiedMfaFactor(user);

    identifyAmplitudeUser({
      'User Role': membership.role,
      'Account ID': organization.id,
      'Account Type': accountType,
      'Subscription Tier': bootstrapSubscription?.planSlug ?? bootstrapSubscription?.planName ?? null,
      'Billing Cycle': bootstrapSubscription?.billingInterval ?? null,
      'Signup Method': readSetting(organization.settings, 'signup_method'),
      'MFA Enabled': mfaEnabled,
      'Onboarding Status': organization ? 'completed' : 'not_started',
      'Primary Compliance Framework': primaryComplianceFramework,
    });
  }, [bootstrapSubscription, membership, organization, user]);

  useEffect(() => {
    if (!user || !organization || !membership) {
      previousContextRef.current = null;
      return;
    }

    const contextProperties = resolveAnalyticsContext({
      organization,
      membership,
      isMspOrg,
      isMspContext,
      mspHomeOrg,
      route: getAnalyticsRouteDescriptor(location.pathname),
    });
    const mspOrg = contextProperties['msp org id'] === organization.id
      ? organization
      : contextProperties['msp org id'] === mspHomeOrg?.id
        ? mspHomeOrg
        : null;
    const customerOrg = contextProperties['customer org id'] === organization.id ? organization : null;
    const supplierOrg = contextProperties['supplier org id'] === organization.id ? organization : null;

    const groups = {
      msp_org: buildGroupDefinition({
        organization: mspOrg,
        orgType: 'msp',
      }),
      customer_org: buildGroupDefinition({
        organization: customerOrg,
        orgType: 'customer',
      }),
      supplier_org: buildGroupDefinition({
        organization: supplierOrg,
        orgType: 'supplier',
      }),
    };
    const groupsKey = JSON.stringify(groups);
    if (lastGroupsKeyRef.current === groupsKey) {
      return;
    }
    const previousContext = previousContextRef.current;
    lastGroupsKeyRef.current = groupsKey;
    setAmplitudeGroups(groups);
    previousContextRef.current = contextProperties;

    if (
      previousContext &&
      previousContext['active org id'] !== contextProperties['active org id']
    ) {
      trackAmplitudeEvent('Organization Switched', {
        'from active org id': previousContext['active org id'],
        'from active org type': previousContext['active org type'],
        'from account relationship': previousContext['account relationship'],
        'to active org id': contextProperties['active org id'],
        'to active org type': contextProperties['active org type'],
        'switch source': contextProperties['account relationship'] === 'msp_customer'
          ? 'msp_customer_login'
          : 'workspace_switcher',
        ...contextProperties,
      });
    }
  }, [isMspContext, isMspOrg, location.pathname, membership, mspHomeOrg, organization, user]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, authSession) => {
      const authUser = authSession?.user ?? null;

      if (event === 'SIGNED_OUT') {
        setPendingSignedIn(null);
        setPendingEmailVerified(null);
        return;
      }

      if (event === 'SIGNED_IN' && authUser?.id) {
        setPendingSignedIn({
          dedupeKey: `amplitude:signed-in:${authUser.id}:${authSession?.access_token ?? 'no-token'}`,
          userId: authUser.id,
          mfaEnabled: hasVerifiedMfaFactor(authUser),
        });
      }

      if (event === 'USER_UPDATED' && authUser?.id && authUser.email_confirmed_at) {
        setPendingEmailVerified({
          dedupeKey: `amplitude:email-verified:${authUser.id}:${authUser.email_confirmed_at}`,
          userId: authUser.id,
          createdAt: authUser.created_at,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!pendingSignedIn || user?.id !== pendingSignedIn.userId || !organization || !membership) {
      return;
    }

    if (oncePerBrowser(sessionStorage, pendingSignedIn.dedupeKey)) {
      trackAmplitudeEvent('Signed In', {
        'login method': 'password',
        'account id': organization.id,
        'user role': membership.role,
        'mfa enabled': pendingSignedIn.mfaEnabled,
      });
    }
    setPendingSignedIn(null);
  }, [membership, organization, pendingSignedIn, user?.id]);

  useEffect(() => {
    if (!pendingEmailVerified || user?.id !== pendingEmailVerified.userId || !organization?.id) {
      return;
    }

    if (oncePerBrowser(localStorage, pendingEmailVerified.dedupeKey)) {
      trackAmplitudeEvent('Email Verified', {
        'account id': organization.id,
        'verification method': 'email_link',
        'time since account created seconds': getSecondsSince(pendingEmailVerified.createdAt),
      });
    }
    setPendingEmailVerified(null);
  }, [organization?.id, pendingEmailVerified, user?.id]);

  return null;
}
